import React from 'react';

export type SlotSelectorConfig<T> = Map<number, T>;

interface UseSlotSelectorOptions<T> {
  /** The total number of slots available */
  totalSlots: number,
  /** Optional initial items to pre-populate the slots. Only the first `totalSlots` items are used. */
  initialItems?: T[],
  /** A key that, when changed, resets the selection and re-seeds from `initialItems` (e.g. the entity id) */
  resetKey?: string | number,
  /** Invoked when the selection changes, with the current `Map<slotIndex, item>` */
  onChange?: (slots: SlotSelectorConfig<T>) => void,
}

interface UseSlotSelectorReturn<T> {
  slots: SlotSelectorConfig<T>,
  assignSlot: (slotIndex: number, item: T) => void,
  deselectSlot: (slotIndex: number) => void,
}

const buildSlotsFromItems = <T>(items: T[] | undefined, totalSlots: number): Map<number, T> => {
  const slots = new Map<number, T>();
  items?.slice(0, totalSlots).forEach((item, index) => {
    slots.set(index, item);
  });
  return slots;
};

/**
 * Manages slot-based item selection as a `Map<slotIndex, item>`.
 *
 * Seeds the slots once from `initialItems` (per `resetKey`), then lets the user
 * mutate them via `assignSlot` / `deselectSlot`. When `resetKey` changes the
 * selection is cleared and re-seeded once the new `initialItems` are available.
 *
 * @example
 * const { slots, assignSlot, deselectSlot } = useSlotSelector({
 *   totalSlots: 3,
 *   initialItems: reviews,
 *   resetKey: agent.id,
 *   onChange: (slots) => console.log(slots),
 * });
 */
export const useSlotSelector = <T>({
  totalSlots,
  initialItems,
  resetKey,
  onChange,
}: UseSlotSelectorOptions<T>): UseSlotSelectorReturn<T> => {
  const [slots, setSlots] = React.useState<SlotSelectorConfig<T>>(new Map());

  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  });

  const skipFirstRef = React.useRef(true);
  React.useEffect(() => {
    if (skipFirstRef.current) {
      skipFirstRef.current = false;
      return;
    }
    onChangeRef.current?.(slots);
  }, [slots]);

  const hasSeededRef = React.useRef(false);

  const prevKeyRef = React.useRef(resetKey);
  React.useEffect(() => {
    if (prevKeyRef.current === resetKey) return;
    prevKeyRef.current = resetKey;
    hasSeededRef.current = false;
    setSlots(new Map());
  }, [resetKey]);

  React.useEffect(() => {
    if (hasSeededRef.current) return;
    if (!initialItems?.length) return;
    hasSeededRef.current = true;
    setSlots(buildSlotsFromItems(initialItems, totalSlots));
  }, [resetKey, initialItems, totalSlots]);

  const assignSlot = React.useCallback((slotIndex: number, item: T) => {
    setSlots((current) => {
      const next = new Map(current);
      next.set(slotIndex, item);
      return next;
    });
  }, []);

  const deselectSlot = React.useCallback((slotIndex: number) => {
    setSlots((current) => {
      const next = new Map(current);
      next.delete(slotIndex);
      return next;
    });
  }, []);

  return { slots, assignSlot, deselectSlot };
};
